import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@/context';

export default function CookieBanner() {
  const { status, acceptCookies, declineCookies } = useCookieConsent();
  const { pathname } = useLocation();

  const isDashboardRoute = pathname.startsWith('/admin') || pathname.startsWith('/dashboard');

  if (status !== 'undecided' || isDashboardRoute) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      >
        <div className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border border-zinc-200 dark:border-zinc-800 shadow-2xl rounded-2xl p-6 md:p-8">
            {/* Background Glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-orange-500/10 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-amber-500/10 blur-3xl rounded-full" />

            <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-orange-500/20">
                <Cookie className="w-6 h-6" />
              </div>

              <div className="flex-grow text-center md:text-left">
                <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-1">
                  We use cookies
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl">
                  We use cookies to improve your experience, analyze our traffic, and provide personalized delivery options. By clicking "Accept All", you agree to our use of cookies. Read our <Link to="/privacy-policy" className="text-orange-500 hover:underline font-medium">Privacy Policy</Link> to learn more.
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button
                  variant="ghost"
                  onClick={declineCookies}
                  className="text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors"
                >
                  Necessary Only
                </Button>
                <Button
                  onClick={acceptCookies}
                  className="px-8 shadow-lg shadow-orange-500/20"
                >
                  Accept All
                </Button>
              </div>

              <button
                onClick={declineCookies}
                className="absolute top-4 right-4 md:static p-2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
